'use client';

import { useState, useEffect } from 'react';
import { getFarmRewardPrices } from '@/lib/price-data/farmPrices';
import { getNativePrices } from '@/lib/price-data/geckoPrices';
import { FARM_PAGE_CONFIG } from '@/constants/project-specific/farms';
import type { FarmRewardPrices, NativeTokenPrices } from '@/constants/project-specific/farms/types';
interface UseFarmPricesResult {
  prices: FarmRewardPrices;
  nativePrices: NativeTokenPrices;
  loading: boolean;
  error: string | null;
  isUsingFallback: boolean;
  refetch: () => Promise<void>;
}
interface UseFarmPricesProps {
  initialPrices?: FarmRewardPrices;
  initialNativePrices?: NativeTokenPrices;
  enableClientFallback?: boolean;
}

/**
 * Hook that manages farm price data with client-side fallback capabilities.
 * If server-side data is missing or invalid, it will attempt to fetch fresh data client-side.
 */
export function useFarmPrices({
  initialPrices = {},
  initialNativePrices = {},
  enableClientFallback = true
}: UseFarmPricesProps = {}): UseFarmPricesResult {
  const [prices, setPrices] = useState<FarmRewardPrices>(initialPrices);
  const [nativePrices, setNativePrices] = useState<NativeTokenPrices>(initialNativePrices);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUsingFallback, setIsUsingFallback] = useState(false);

  // Check if we need to fetch client-side data
  const needsClientFetch = () => {
    if (!enableClientFallback) return false;

    // Check if farm reward prices are missing or empty
    const hasValidFarmPrices = Object.keys(prices).length > 0 && Object.values(prices).some(chainPrices => chainPrices.length > 0);

    // Check if native prices are missing or empty  
    const hasValidNativePrices = Object.keys(nativePrices).length > 0 && Object.values(nativePrices).some(price => price.priceUsd > 0);
    return !hasValidFarmPrices || !hasValidNativePrices;
  };
  const fetchClientSideData = async () => {
    if (!enableClientFallback) return;
    try {
      setLoading(true);
      setError(null);
      setIsUsingFallback(true);
      console.log('🔄 Attempting client-side price data fetch due to insufficient server data...');
      const chainIds = FARM_PAGE_CONFIG.chainIds;

      // Fetch both prices concurrently
      const [newPrices, newNativePrices] = await Promise.all([getFarmRewardPrices().catch(err => {
        console.warn('⚠️ Client-side farm prices fetch failed:', err);
        return prices; // Keep existing data
      }), getNativePrices(chainIds).catch(err_0 => {
        console.warn('⚠️ Client-side native prices fetch failed:', err_0);
        return nativePrices; // Keep existing data
      })]);

      // Update state with new data
      setPrices(newPrices);
      setNativePrices(newNativePrices);

      // Validate that we got useful data
      const hasValidFarmPrices_0 = Object.keys(newPrices).length > 0 && Object.values(newPrices).some(chainPrices_0 => chainPrices_0.length > 0);
      const hasValidNativePrices_0 = Object.keys(newNativePrices).length > 0 && Object.values(newNativePrices).some(price_0 => price_0.priceUsd > 0);
      if (hasValidFarmPrices_0 && hasValidNativePrices_0) {
        console.log('✅ Successfully fetched client-side price data');
      } else {
        console.warn('⚠️ Client-side fetch completed but data may be incomplete');
      }
    } catch (fetchError) {
      const errorMessage = fetchError instanceof Error ? fetchError.message : 'Failed to fetch price data';
      console.error('❌ Client-side price fetch failed:', fetchError);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  const refetch = async () => {
    await fetchClientSideData();
  };

  // Check if we need client-side data on mount
  useEffect(() => {
    if (needsClientFetch()) {
      console.log('📊 Insufficient server-side price data detected, initiating client-side fetch...');
      fetchClientSideData();
    }
  }, []);
  return {
    prices,
    nativePrices,
    loading,
    error,
    isUsingFallback,
    refetch
  };
}